import * as Sentry from "@sentry/vue";
import type { App } from "vue";
import type { Router } from "vue-router";

export interface SentryScope {
  tags?: Record<string, string>
  user?: {
    id?: string
    userName?: string
    role?: string
    newUi?: boolean
  }
}

export class SentryNode {
  private app: App;
  private router: Router;
  private isSentryEnabled: boolean;
  private sentryDSN: string;
  private defaultScope: SentryScope;

  constructor(app: App, router: Router, defaultScope: SentryScope = {}) {
    this.app = app;
    this.router = router;
    this.defaultScope = defaultScope;

    this.isSentryEnabled = import.meta.env.VITE_SENTRY_ENABLED?.toUpperCase() === "TRUE";

    this.sentryDSN = import.meta.env.VITE_SENTRY_DSN;
  }

  init(): void {
    if (!this.isSentryEnabled || !this.sentryDSN) {
      return;
    }

    Sentry.init({
      app: this.app,
      dsn: this.sentryDSN,
      debug: false,
      integrations: [
        Sentry.browserTracingIntegration({
          router: this.router,
        }),
        Sentry.replayIntegration(),
      ],
      tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 0.3,
    });

    this.applyDefaultScope();
  }

  private applyDefaultScope(): void {
    const currentScope = Sentry.getCurrentScope();

    if (this.defaultScope.user) {
      currentScope.setUser(this.defaultScope.user);
    }

    if (this.defaultScope.tags) {
      Object.entries(this.defaultScope.tags).forEach(([key, value]) => {
        currentScope.setTag(key, value);
      });
    }
  }

  setScope(scope: SentryScope): void {
    const currentScope = Sentry.getCurrentScope();

    if (scope.user) {
      currentScope.setUser(scope.user);
    }

    if (scope.tags) {
      Object.entries(scope.tags).forEach(([key, value]) => {
        currentScope.setTag(key, value);
      });
    }
  }
}
